import {
  enableProdMode,
  LOCALE_ID,
  APP_INITIALIZER,
  ErrorHandler,
  PLATFORM_ID,
  importProvidersFrom
} from '@angular/core';
import { environment } from './environments/environment';
import { hmrBootstrap } from './hmr';
import { AppComponent } from './app/app.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CdkTableModule } from '@angular/cdk/table';
import { MatTableModule } from '@angular/material/table';
import { ToastrModule } from 'ngx-toastr';
import { provideAnimations } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CookieModule } from 'ngx-cookie';
import { WindowInjection } from './app/common/window';
import { LogLevel } from './app/log-levels';
import { SettingsService } from './app/common/services/settings.service';
import { SessionStatusInterceptor } from './app/common/interceptors/sessionStatus.interceptor';
import { AuthInterceptor } from './app/common/interceptors/auth.interceptor';
import { CSRFInterceptor } from './app/common/interceptors/csrf.interceptor';
import { Title, BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import {
  HttpClient,
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  withInterceptorsFromDi,
  provideHttpClient
} from '@angular/common/http';
import { CurrencyPipe, isPlatformBrowser, registerLocaleData } from '@angular/common';
import * as Sentry from '@sentry/angular';
import { graphqlProvider } from './app/graphql.provider';
import localePt from '@angular/common/locales/pt';
import localeEn from '@angular/common/locales/en';
import localeEnAU from '@angular/common/locales/en-AU';
import localeEnGB from '@angular/common/locales/en-GB';
import localeptMZ from '@angular/common/locales/pt-MZ';
import localeEnSE from '@angular/common/locales/en-SE';
import { ApolloModule } from 'apollo-angular';
import { provideRouter, withPreloading } from '@angular/router';
import { MainPreloader, routes } from './app/app-routing.module';
import { MixpanelService } from './app/common/services/mixpanel.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { GuestAuthInterceptor } from './app/guest/guest-auth-interceptor';
import { WeglotService } from './app/common/services/weglot.service';

const defaultToastConfig = {
  timeOut: 10000,
  positionClass: 'toast-above-intercom'
};

const FORCE_SENTRY = false; // Set to true temporarily when you want to test Sentry in dev

if (environment.production) {
  enableProdMode();
}

const bootstrap = () =>
  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(
        BrowserModule,
        CookieModule.forRoot(),
        FormsModule,
        ReactiveFormsModule,
        ToastrModule.forRoot(defaultToastConfig),
        MatTableModule,
        CdkTableModule,
        MatInputModule,
        MatFormFieldModule,
        MatDatepickerModule,
        FontAwesomeModule,
        ApolloModule
      ),
      provideRouter(routes, withPreloading(MainPreloader)),
      CurrencyPipe,
      HttpClient,
      Title,
      ...graphqlProvider,
      { provide: HTTP_INTERCEPTORS, useClass: GuestAuthInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: CSRFInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: SessionStatusInterceptor, multi: true },
      { provide: LOCALE_ID, deps: [SettingsService], useFactory: (settingService) => settingService.getLanguage() },
      { provide: APP_INITIALIZER, useFactory: provideLogInit, multi: true },
      {
        provide: WindowInjection,
        deps: [PLATFORM_ID],
        useFactory: (platformId) => (isPlatformBrowser(platformId) ? window : {})
      },
      { provide: ErrorHandler, useClass: SentryErrorHandler },
      {
        provide: Sentry.TraceService,
        deps: []
      },
      provideAnimations(),
      provideHttpClient(withInterceptorsFromDi()),
      MixpanelService,
      {
        provide: 'MixpanelInitializer',
        useFactory: initializeMixpanel,
        deps: [MixpanelService]
      },
      WeglotService
    ]
  });

function shouldCaptureErrors(): boolean {
  const environmentName = window['staticConfig']?.environment || environment.sentryOpts?.environment;
  return (
    environmentName === 'production' ||
    environmentName === 'staging-consumer' ||
    environmentName === 'builder-consumer' ||
    environmentName === 'consumer-prod' ||
    environment.production ||
    FORCE_SENTRY
  );
}

export class SentryErrorHandler implements ErrorHandler {
  constructor() {}

  extractError(error) {
    if (error && error.ngOriginalError) {
      error = error.ngOriginalError;
    }

    // We can handle messages and Error objects directly.
    if (typeof error === 'string' || error instanceof Error) {
      return error;
    }

    // If it's http module error, extract as much information from it as we can.
    if (error instanceof HttpErrorResponse) {
      // The `error` property of http exception can be either an `Error` object, which we can use directly...
      if (error.error instanceof Error) {
        return error.error;
      }

      // ... or an`ErrorEvent`, which can provide us with the message but no stack...
      if (error.error instanceof ErrorEvent) {
        return error.error.message;
      }

      // ...or the request body itself, which we can use as a message instead.
      if (typeof error.error === 'string') {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }

      // If we don't have any detailed information, fallback to the request message itself.
      return error.message;
    }

    // Skip if there's no error, and let user decide what to do with it.
    return null;
  }

  handleError(error) {
    const extractedError = this.extractError(error) || 'Handled unknown error';

    // Only send errors to Sentry in production & Staging
    if (shouldCaptureErrors()) {
      // Capture handled exception and send it to Sentry.
      const eventId = Sentry.captureException(extractedError);
    }

    // When in development mode, log the error to console for immediate feedback.
    if (!environment.production) {
      console.error(extractedError);
    }
  }
}

export function overrideConsoleMethod(methodName: string, level: LogLevel, severity: Sentry.SeverityLevel) {
  const originalMethod = console[methodName].bind(console);
  console[methodName] = function (...items) {
    if (environment.logLevel > level) {
      return;
    }
    if (shouldCaptureErrors()) {
      Sentry.captureMessage(formatLogItems(items), severity);
    } else {
      originalMethod.apply(console, arguments);
    }
  };
}

export function formatLogItems(items) {
  if (items.some((i) => typeof i === 'object')) {
    return items.map((i) => JSON.stringify(i, null, 2)).join('\n');
  }
  return items.join(' ');
}

export function provideLogInit(): () => void {
  return () => {
    overrideConsoleMethod('log', LogLevel.ALL, 'debug');
    overrideConsoleMethod('info', LogLevel.INFO, 'info');
    overrideConsoleMethod('warn', LogLevel.WARN, 'warning');
    overrideConsoleMethod('error', LogLevel.ERROR, 'error');
  };
}

export function initializeMixpanel(mixpanelService: MixpanelService) {
  return () => mixpanelService.init();
}
if (!window.hasOwnProperty('staticConfig')) {
  // staticConfig set by ./server/config/config.js.template
  // If not set by config.js then utilize buildtime sentry environment
  window['staticConfig'] = environment.sentryOpts;
}

Sentry.init({
  ...window['staticConfig'],
  // Enable in production, staging, builder or when manually forced
  enabled: shouldCaptureErrors(),
  // disable XMLHttpRequest wrapping, as we are going to handle http module exceptions manually in Angular's ErrorHandler
  // and we don't want it to capture the same error twice.
  integrations: [
    Sentry.browserApiErrorsIntegration({
      XMLHttpRequest: false
    }),
    // Add browser tracing for performance monitoring
    Sentry.browserTracingIntegration()
  ],
  // Set trace sample rate for performance monitoring
  tracesSampleRate: environment.production ? 0.2 : 1.0
});

registerLocaleData(localePt);
registerLocaleData(localeEn);
registerLocaleData(localeEnAU);
registerLocaleData(localeEnGB);
registerLocaleData(localeptMZ);
registerLocaleData(localeEnSE);

if (environment.hmr) {
  if (module['hot']) {
    hmrBootstrap(module, bootstrap);
  } else {
    console.error('HMR is not enabled for webpack-dev-server');
    console.log('Are you using the --hmr flag for ng serve?');
  }
} else {
  bootstrap();
}
